import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'prontostay-theme';

function getStoredTheme(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // SSR / incognito — fall through
  }
  return 'system';
}

function getResolvedTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode !== 'system') return mode;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(mode: ThemeMode) {
  const resolved = getResolvedTheme(mode);
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
}

// Tiny external store so all consumers stay in sync
let currentMode: ThemeMode = getStoredTheme();
const listeners = new Set<() => void>();

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

function getSnapshot() {
  return currentMode;
}

function setMode(next: ThemeMode) {
  currentMode = next;
  try { localStorage.setItem(STORAGE_KEY, next); } catch { /* noop */ }
  applyTheme(next);
  listeners.forEach((cb) => cb());
}

// Apply on first load (before React hydrates)
applyTheme(currentMode);

export function useTheme() {
  const mode = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  // Listen for OS preference changes when in 'system' mode
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      if (currentMode === 'system') applyTheme('system');
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const setTheme = useCallback((next: ThemeMode) => setMode(next), []);

  const toggle = useCallback(() => {
    const resolved = getResolvedTheme(currentMode);
    setMode(resolved === 'dark' ? 'light' : 'dark');
  }, []);

  const resolvedTheme = useMemo(() => getResolvedTheme(mode), [mode]);

  return { mode, resolvedTheme, setTheme, toggle } as const;
}
